import type { Lang } from '../types';

/**
 * Text for the admin "Reset password" button and dialog (issue #124). Placeholders in curly braces
 * ({name}, {email}) are filled by the component; they never carry a password.
 */
export interface AdminPasswordResetText {
  button: string;
  title: string;
  intro: string;
  newPasswordLabel: string;
  confirmPasswordLabel: string;
  hint: string;
  errorTooShort: string;
  errorTooLong: string;
  errorMismatch: string;
  submit: string;
  submitting: string;
  cancel: string;
  success: string;
  errorWeak: string;
  errorSelf: string;
  errorAdminDisabled: string;
  errorAnonymized: string;
  errorNotFound: string;
  errorForbidden: string;
  errorGeneric: string;
}

export const adminPasswordResetText: Record<Lang, AdminPasswordResetText> = {
  cs: {
    button: 'Obnovit heslo',
    title: 'Obnovit heslo pro {name} ({email})',
    intro: 'Uživatel bude všude odhlášen a přihlásí se novým heslem. Předejte mu ho bezpečným kanálem.',
    newPasswordLabel: 'Nové heslo',
    confirmPasswordLabel: 'Potvrďte nové heslo',
    hint: 'Alespoň 8 znaků, nejvýše 72 bajtů.',
    errorTooShort: 'Heslo musí mít alespoň 8 znaků.',
    errorTooLong: 'Heslo je příliš dlouhé (nejvýše 72 bajtů; znaky s diakritikou zabírají více).',
    errorMismatch: 'Hesla se neshodují.',
    submit: 'Nastavit heslo',
    submitting: 'Ukládám…',
    cancel: 'Zrušit',
    success: 'Heslo uživatele {name} bylo obnoveno. Všechna jeho přihlášení byla ukončena.',
    errorWeak: 'Heslo nesplňuje pravidla (alespoň 8 znaků, nejvýše 72 bajtů).',
    errorSelf: 'Vlastní heslo zde změnit nelze. Použijte obrazovku pro změnu hesla.',
    errorAdminDisabled: 'Obnovení hesla účtu administrátora je na tomto serveru vypnuté.',
    errorAnonymized: 'Tento účet je anonymizovaný, jeho heslo nelze obnovit.',
    errorNotFound: 'Uživatel nebyl nalezen.',
    errorForbidden: 'K této akci nemáte oprávnění.',
    errorGeneric: 'Heslo se nepodařilo obnovit. Zkuste to prosím znovu.',
  },
  en: {
    button: 'Reset password',
    title: 'Reset password for {name} ({email})',
    intro: 'The user will be signed out everywhere and must log in with the new password. Share it with them over a secure channel.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    hint: 'At least 8 characters, at most 72 bytes.',
    errorTooShort: 'The password must be at least 8 characters.',
    errorTooLong: 'The password is too long (at most 72 bytes; accented characters take more).',
    errorMismatch: 'The passwords do not match.',
    submit: 'Set password',
    submitting: 'Saving…',
    cancel: 'Cancel',
    success: "{name}'s password was reset. All of their sessions were signed out.",
    errorWeak: 'The password does not meet the rules (at least 8 characters, at most 72 bytes).',
    errorSelf: 'You cannot reset your own password here. Use the change-password screen.',
    errorAdminDisabled: 'Resetting the password of an ADMIN account is disabled on this server.',
    errorAnonymized: 'This account is anonymized; its password cannot be reset.',
    errorNotFound: 'User not found.',
    errorForbidden: 'You are not allowed to do this.',
    errorGeneric: 'Could not reset the password. Please try again.',
  },
  vi: {
    button: 'Đặt lại mật khẩu',
    title: 'Đặt lại mật khẩu cho {name} ({email})',
    intro: 'Người dùng sẽ bị đăng xuất khỏi mọi nơi và phải đăng nhập bằng mật khẩu mới. Hãy gửi mật khẩu cho họ qua kênh an toàn.',
    newPasswordLabel: 'Mật khẩu mới',
    confirmPasswordLabel: 'Xác nhận mật khẩu mới',
    hint: 'Ít nhất 8 ký tự, tối đa 72 byte.',
    errorTooShort: 'Mật khẩu phải có ít nhất 8 ký tự.',
    errorTooLong: 'Mật khẩu quá dài (tối đa 72 byte; ký tự có dấu chiếm nhiều hơn).',
    errorMismatch: 'Mật khẩu không khớp.',
    submit: 'Đặt mật khẩu',
    submitting: 'Đang lưu…',
    cancel: 'Hủy',
    success: 'Mật khẩu của {name} đã được đặt lại. Mọi phiên đăng nhập của họ đã bị đăng xuất.',
    errorWeak: 'Mật khẩu không đáp ứng quy tắc (ít nhất 8 ký tự, tối đa 72 byte).',
    errorSelf: 'Bạn không thể đặt lại mật khẩu của chính mình ở đây. Hãy dùng màn hình đổi mật khẩu.',
    errorAdminDisabled: 'Việc đặt lại mật khẩu của tài khoản ADMIN đã bị tắt trên máy chủ này.',
    errorAnonymized: 'Tài khoản này đã được ẩn danh nên không thể đặt lại mật khẩu.',
    errorNotFound: 'Không tìm thấy người dùng.',
    errorForbidden: 'Bạn không có quyền thực hiện thao tác này.',
    errorGeneric: 'Không thể đặt lại mật khẩu. Vui lòng thử lại.',
  },
  uk: {
    button: 'Скинути пароль',
    title: 'Скинути пароль для {name} ({email})',
    intro: 'Користувача буде виведено з усіх сеансів, і він входитиме з новим паролем. Передайте його безпечним каналом.',
    newPasswordLabel: 'Новий пароль',
    confirmPasswordLabel: 'Підтвердьте новий пароль',
    hint: 'Щонайменше 8 символів, щонайбільше 72 байти.',
    errorTooShort: 'Пароль має містити щонайменше 8 символів.',
    errorTooLong: 'Пароль задовгий (щонайбільше 72 байти; літери з діакритикою займають більше).',
    errorMismatch: 'Паролі не збігаються.',
    submit: 'Встановити пароль',
    submitting: 'Збереження…',
    cancel: 'Скасувати',
    success: 'Пароль користувача {name} скинуто. Усі його сеанси завершено.',
    errorWeak: 'Пароль не відповідає правилам (щонайменше 8 символів, щонайбільше 72 байти).',
    errorSelf: 'Власний пароль тут змінити не можна. Скористайтеся екраном зміни пароля.',
    errorAdminDisabled: 'Скидання пароля облікового запису ADMIN вимкнено на цьому сервері.',
    errorAnonymized: 'Цей обліковий запис анонімізовано, його пароль скинути не можна.',
    errorNotFound: 'Користувача не знайдено.',
    errorForbidden: 'У вас немає дозволу на цю дію.',
    errorGeneric: 'Не вдалося скинути пароль. Спробуйте ще раз.',
  },
};

export const fillTemplate = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '');
